// ============================================================================
// PARTNER MASTER SCREEN — FSA Partners master (NCP-13/14)
// ============================================================================
// Lists fsa.tbl_partner. Per-client (firm) roster — the Owners' Capital
// Account Note auto-fills partner rows + Share % from here, and the
// signature panel pulls partners flagged is_signatory.
// Follows the FSA Engagements list pattern (FsaEngagementListScreen):
// list ↔ grid view toggle, ColumnSelector, filter count badge,
// MinimizedModalStrip, allowedActions gating.

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  MinimizedModalStrip,
  ColumnSelector,
  type ColumnDefinition,
} from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useFsaPartners } from 'hooks/api/fsa/useFsaPartners';
import { fsaPartnerService } from 'api/services/fsa/fsaPartnerService';
import { clientService } from 'api/services/clientService';
import type { Partner, PartnerSearchRequest } from 'api/types/fsa/partner.types';
import PartnerFormModal from 'components/modals/modules/fsa/PartnerFormModal';

type ViewMode = 'list' | 'grid';

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'ACTIVE', label: 'Active only' },
  { value: 'INACTIVE', label: 'Inactive only' },
];

// Column definitions powering the ColumnSelector (toolbar gear menu).
const columnDefinitions: ColumnDefinition[] = [
  { id: 'clientLegalName',    label: 'Firm',           field: 'clientLegalName',    required: true,  defaultVisible: true, sortable: true },
  { id: 'partnerName',        label: 'Partner',        field: 'partnerName',        required: true,  defaultVisible: true, sortable: true },
  { id: 'designation',        label: 'Designation',    field: 'designation',        required: false, defaultVisible: true, sortable: true },
  { id: 'pan',                label: 'PAN',            field: 'pan',                required: false, defaultVisible: true, sortable: false },
  { id: 'profitSharingRatio', label: 'Share %',        field: 'profitSharingRatio', required: false, defaultVisible: true, sortable: true },
  { id: 'isSignatory',        label: 'Signatory',      field: 'isSignatory',        required: false, defaultVisible: true, sortable: true },
  { id: 'effectiveFrom',      label: 'Effective From', field: 'effectiveFrom',      required: false, defaultVisible: true, sortable: true },
  { id: 'effectiveTo',        label: 'Effective To',   field: 'effectiveTo',        required: false, defaultVisible: true, sortable: true },
  { id: 'status',             label: 'Status',         field: 'statusName',         required: false, defaultVisible: true, sortable: true },
];

const COLUMN_PREF_KEY = 'fsa-partner-columns';

const formatDate = (iso?: string | null): string => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '-'; }
};

interface FsaPartnerMasterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  allowedActions?: string[];
}

const FsaPartnerMasterScreen: React.FC<FsaPartnerMasterScreenProps> = ({
  moduleIcon,
  tabId,
  moduleName,
  allowedActions = [],
}) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  const [queryParams, setQueryParams] = useState<PartnerSearchRequest>({ page: 1, pageSize: 25 });
  const { data, loading, pagination, refetch } = useFsaPartners(queryParams);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [clientFilter, setClientFilter] = useState<string>('');
  const [nameFilter, setNameFilter] = useState('');
  const [designationFilter, setDesignationFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Column visibility — persisted to localStorage (per the enterprise list pattern).
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() =>
    columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id)
  );

  useEffect(() => {
    const saved = localStorage.getItem(COLUMN_PREF_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) setVisibleColumns(parsed);
      } catch { /* ignore */ }
    }
  }, []);

  const handleApplyColumns = useCallback((cols: string[]) => {
    setVisibleColumns(cols);
    localStorage.setItem(COLUMN_PREF_KEY, JSON.stringify(cols));
  }, []);

  const handleResetColumns = useCallback(() => {
    const defaults = columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id);
    setVisibleColumns(defaults);
    localStorage.removeItem(COLUMN_PREF_KEY);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setQueryParams(prev => ({ ...prev, searchTerm: searchQuery || undefined, page: 1 }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const handlePageChange = useCallback((idx: number) => {
    setQueryParams(prev => ({ ...prev, page: idx + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((size: number) => {
    setQueryParams(prev => ({ ...prev, pageSize: size, page: 1 }));
  }, []);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (queryParams.clientId) count++;
    if (queryParams.partnerName) count++;
    if (queryParams.designation) count++;
    if (queryParams.statusCode) count++;
    return count;
  }, [queryParams.clientId, queryParams.partnerName, queryParams.designation, queryParams.statusCode]);

  const applyFilters = useCallback(() => {
    setQueryParams(prev => ({
      ...prev,
      clientId:        clientFilter || undefined,
      partnerName:     nameFilter || undefined,
      designation:     designationFilter || undefined,
      statusCode:      statusFilter || undefined,
      includeInactive: statusFilter === 'INACTIVE',
      page:            1,
    }));
    setFilterOpen(false);
  }, [clientFilter, nameFilter, designationFilter, statusFilter]);

  const clearFilters = useCallback(() => {
    setClientFilter('');
    setNameFilter('');
    setDesignationFilter('');
    setStatusFilter('');
    setQueryParams(prev => ({
      ...prev,
      clientId: undefined, partnerName: undefined, designation: undefined, statusCode: undefined, includeInactive: undefined, page: 1,
    }));
  }, []);

  const loadClientOptions = useCallback(async (search: string) => {
    const resp = await clientService.getAll({ page: 1, pageSize: 50, searchTerm: search || undefined });
    return [{ value: '', label: 'All firms' }, ...resp.data.map(c => ({
      value: String(c.id),
      label: c.legalName ? `${c.legalName} (${c.code})` : `${c.name} (${c.code})`,
    }))];
  }, []);

  // Modal state
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);

  // Minimize support — multi-instance Add + restore of minimized sessions
  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const handleRestoreModal = useCallback(
    (restoredMode: 'create' | 'edit', instanceId?: string, recordId?: string) => {
      if (instanceId) setCreateInstanceId(instanceId);
      if (recordId) {
        const rec = data.find((r) => String(r.id) === recordId);
        if (rec) setSelectedPartner(rec);
      }
      setFormMode(restoredMode);
      setFormModalOpen(true);
    },
    [data]
  );

  // Delete state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const columns: SyutGridColumn<Partner>[] = useMemo(() => [
    { id: 'clientLegalName', header: 'Firm', accessorKey: 'clientLegalName', enableSorting: true, size: 240 },
    { id: 'partnerName', header: 'Partner', accessorKey: 'partnerName', enableSorting: true, size: 200 },
    { id: 'designation', header: 'Designation', accessorKey: 'designation', enableSorting: true, size: 150 },
    { id: 'pan', header: 'PAN', accessorKey: 'pan', enableSorting: false, size: 110 },
    { id: 'profitSharingRatio', header: 'Share %',
      accessorFn: (row: Partner) => row.profitSharingRatio,
      cell: ({ row }: { row: { original: Partner } }) => (
        <span style={{ textAlign: 'right', display: 'block' }}>
          {row.original.profitSharingRatio != null ? `${row.original.profitSharingRatio}%` : '-'}
        </span>
      ),
      size: 90, enableSorting: true },
    { id: 'isSignatory', header: 'Signatory',
      accessorFn: (row: Partner) => row.isSignatory,
      cell: ({ row }: { row: { original: Partner } }) => (
        <span style={{ color: row.original.isSignatory ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
          {row.original.isSignatory ? 'Yes' : 'No'}
        </span>
      ),
      size: 90, enableSorting: true },
    { id: 'effectiveFrom', header: 'Effective From',
      accessorFn: (row: Partner) => row.effectiveFrom,
      cell: ({ row }: { row: { original: Partner } }) => <span>{formatDate(row.original.effectiveFrom)}</span>,
      size: 120, enableSorting: true },
    { id: 'effectiveTo', header: 'Effective To',
      accessorFn: (row: Partner) => row.effectiveTo,
      cell: ({ row }: { row: { original: Partner } }) => (
        <span style={{ color: row.original.effectiveTo ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
          {row.original.effectiveTo ? formatDate(row.original.effectiveTo) : 'Active'}
        </span>
      ),
      size: 120, enableSorting: true },
    { id: 'status', header: 'Status', accessorKey: 'statusName', enableSorting: true, size: 100,
      cell: ({ row }: { row: { original: Partner } }) => {
        const isActive = row.original.statusCode === 'ACTIVE';
        return (
          <span className={`badge badge-${isActive ? 'active' : 'inactive'}`}>
            {row.original.statusName || (isActive ? 'Active' : 'Inactive')}
          </span>
        );
      } },
  ], []);

  const visibleTableColumns = useMemo<SyutGridColumn<Partner>[]>(
    () => columns.filter((col) => visibleColumns.includes(col.id as string)),
    [columns, visibleColumns]
  );

  // If the same record is already minimized, restore it instead of opening fresh
  const handleEdit = useCallback((row: Partner) => {
    const modalId = tabId ? `${tabId}-form-modal-edit-${String(row.id)}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId);
      modal?.onRestore?.();
      return;
    }
    setSelectedPartner(row);
    setFormMode('edit');
    setFormModalOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const handleDeleteClick = useCallback((row: Partner) => {
    setSelectedPartner(row);
    setDeleteModalOpen(true);
  }, []);

  const getRowActions = useCallback((row: Partner): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [];
    if (allowedActions.includes('edit')) {
      items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={16} />, onClick: () => handleEdit(row) });
    }
    if (allowedActions.includes('delete')) {
      items.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={16} />, onClick: () => handleDeleteClick(row), variant: 'danger' });
    }
    return items;
  }, [allowedActions, handleEdit, handleDeleteClick]);

  // Each Add opens a fresh instance (multi-instance minimize)
  const handleAdd = useCallback(() => {
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setSelectedPartner(null);
    setFormMode('create');
    setFormModalOpen(true);
  }, []);

  const actionButtons: ActionButton[] = useMemo(() => [
    ...(allowedActions.includes('add') || allowedActions.includes('create') ? [{
      id: 'add',
      icon: <IconSystem.add size={18} />,
      label: 'Add',
      tooltip: 'Add partner',
      onClick: handleAdd,
      variant: 'icon' as const,
    }] : []),
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      tooltip: 'Refresh',
      onClick: () => refetch(),
      variant: 'icon' as const,
    },
  ], [allowedActions, handleAdd, refetch]);

  const handleConfirmDelete = useCallback(async () => {
    if (!selectedPartner) return;
    setIsDeleting(true);
    try {
      await fsaPartnerService.remove(selectedPartner.id);
      setIsDeleting(false);
      setDeleteModalOpen(false);
      notificationService.toast.success(`"${selectedPartner.partnerName}" deleted successfully.`);
      setSelectedPartner(null);
      refetch();
    } catch (err: any) {
      setIsDeleting(false);
      notificationService.toast.error(err?.response?.data?.message ?? 'Delete failed.', 6000);
    }
  }, [selectedPartner, refetch]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Partners"
        description="Per-firm roster of partners (PSR + signatory). Auto-fills the Owners' Capital Account Note and drives the partnership signature block."
        icon={
          moduleIcon && (IconSystem as any)[moduleIcon]
            ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
            : <IconSystem.users size={18} />
        }
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search partners…',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: [
            { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List view' },
            { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid view' },
          ],
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen || activeFilterCount > 0,
          activeFilterCount,
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={
          <ColumnSelector
            columns={columnDefinitions}
            visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {viewMode === 'list' && (
              <SyutGrid
                data={data}
                columns={visibleTableColumns}
                enableRowSelection
                enablePagination
                manualPagination
                totalCount={pagination.totalItems}
                currentPage={(queryParams.page ?? 1) - 1}
                pageSize={queryParams.pageSize ?? 25}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                enableSorting
                rowActions={getRowActions}
                emptyMessage={loading ? 'Loading partners…' : 'No partners found'}
              />
            )}

            {viewMode === 'grid' && (
              <div
                className="syut-grid-component"
                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
              >
                <div
                  style={{
                    padding: '1rem',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                    gap: '1.5rem',
                    flex: 1,
                    overflow: 'auto',
                    alignContent: 'start',
                  }}
                >
                  {data.length === 0 && (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                      {loading ? 'Loading partners…' : 'No partners found'}
                    </div>
                  )}
                  {data.map((item) => {
                    const isActive = item.statusCode === 'ACTIVE';
                    return (
                      <div
                        key={String(item.id)}
                        style={{
                          background: 'var(--bg-primary)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '8px',
                          padding: '1rem',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.5rem',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                              {item.clientLegalName || '-'}
                            </div>
                            <div style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                              {item.partnerName || '-'}
                            </div>
                          </div>
                          <span className={`badge badge-${isActive ? 'active' : 'inactive'}`}>
                            {item.statusName || (isActive ? 'Active' : 'Inactive')}
                          </span>
                        </div>
                        <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                          {item.designation || '-'}{item.pan ? ` · PAN ${item.pan}` : ''}
                        </div>
                        <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                          Share: {item.profitSharingRatio != null ? `${item.profitSharingRatio}%` : '-'} · Signatory: {item.isSignatory ? 'Yes' : 'No'}
                        </div>
                        <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                          <span>From: {formatDate(item.effectiveFrom)}</span>
                          <span>·</span>
                          <span>To: {item.effectiveTo ? formatDate(item.effectiveTo) : 'Active'}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label="Firm"
                  value={clientFilter}
                  onChange={(val) => setClientFilter((val as string) || '')}
                  isAsync
                  loadOptions={loadClientOptions}
                  defaultOptions
                  cacheOptions={false}
                  isClearable
                  size="sm"
                  placeholder="All firms"
                />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Partner Name</label>
                <input type="text" className="form-input" value={nameFilter}
                  onChange={(e) => setNameFilter(e.target.value)}
                  placeholder="Filter by name…"
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Designation</label>
                <input type="text" className="form-input" value={designationFilter}
                  onChange={(e) => setDesignationFilter(e.target.value)}
                  placeholder="Filter by designation…"
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <SyutSelect
                  label="Status"
                  options={STATUS_FILTER_OPTIONS}
                  value={statusFilter}
                  onChange={(val) => setStatusFilter((val as string) || '')}
                  isClearable={false}
                  size="sm"
                />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={deleteModalOpen}
        title="Confirm Delete"
        message={`Delete partner "${selectedPartner?.partnerName}"? Existing engagement data that references this partner keeps its saved values, but new engagements won't pick them up.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting…"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteModalOpen(false)}
      />

      <PartnerFormModal
        isOpen={formModalOpen}
        mode={formMode}
        record={selectedPartner}
        onSaved={refetch}
        onClose={() => setFormModalOpen(false)}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        createInstanceId={createInstanceId}
        minimizeRef={formModalMinimizeRef}
      />
    </div>
  );
};

export default FsaPartnerMasterScreen;
